import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { appId, db } from '@/lib/firebase';
import { pushAnalyticsEvent } from '@/lib/analytics';
import { resolveDonationFrequencyFromFirestore } from '@/lib/donation-frequency';
import { MonthlyWarriorTeaser } from '@/components/donate/MonthlyWarriorTeaser';
import { useDonorRollup } from '@/lib/use-donor-rollup';
import { useWarriorNumber } from '@/lib/use-warrior-number';
import {
  getEffectiveDonationFrequency,
  getEffectiveStripeSubscriptionId,
  useEnrichedDonationFrequencies,
} from '@/lib/use-enriched-donation-frequencies';

interface CustomerBillingPageProps {
  userId: string;
}

interface DonationRow {
  id: string;
  amount: number;
  currency: string;
  status: string;
  donationFrequency: 'monthly' | 'one_time';
  stripeSubscriptionId?: string | null;
  donatedAt?: { toDate: () => Date } | null;
  receiptEmailSentAt?: { toDate: () => Date } | null;
  receiptEmailStatus?: 'sent' | 'pending' | 'failed';
  receiptEmailFailureReason?: string | null;
  donorIdentityStatus?: 'guest_unclaimed' | 'claimed';
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((amount || 0) / 100);
}

function formatDate(ts?: { toDate: () => Date } | null): string {
  if (!ts) return '—';
  try {
    return ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

export function CustomerBillingPage({ userId }: CustomerBillingPageProps): JSX.Element {
  const [donations, setDonations] = useState<DonationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { donorRecord } = useDonorRollup(userId, 'CustomerBillingPage');
  const { warriorNumber } = useWarriorNumber(userId, 'CustomerBillingPage');
  const { enrichedById, enriching: enrichingDonationFrequencies } =
    useEnrichedDonationFrequencies(donations);

  const donationsWithEffectiveFrequency = useMemo(
    () =>
      donations.map((donation) => ({
        ...donation,
        donationFrequency: getEffectiveDonationFrequency(donation, enrichedById),
        stripeSubscriptionId: getEffectiveStripeSubscriptionId(donation, enrichedById),
      })),
    [donations, enrichedById]
  );

  useEffect(() => {
    const donationsQuery = query(collection(db, 'artifacts', appId, 'donations'), where('userId', '==', userId));

    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(donationsQuery, (snap) => {
        const mapped = snap.docs.map((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const amountFromTotal =
            typeof data.amountTotal === 'number'
              ? data.amountTotal
              : typeof data.amount === 'number'
                ? data.amount
                : 0;
          const status =
            (typeof data.paymentStatus === 'string' ? data.paymentStatus : null) ??
            (typeof data.status === 'string' ? data.status : null) ??
            'unknown';

          return {
            id: docSnap.id,
            amount: amountFromTotal,
            currency: typeof data.currency === 'string' ? data.currency : 'usd',
            status,
            donationFrequency: resolveDonationFrequencyFromFirestore(data),
            stripeSubscriptionId:
              typeof data.stripeSubscriptionId === 'string' ? data.stripeSubscriptionId : null,
            donatedAt:
              (data.paidAt as { toDate: () => Date } | undefined) ??
              (data.createdAt as { toDate: () => Date } | undefined) ??
              null,
            receiptEmailSentAt:
              (data.receiptEmailSentAt as { toDate: () => Date } | undefined) ?? null,
            receiptEmailStatus:
              data.receiptEmailStatus === 'sent' ||
              data.receiptEmailStatus === 'pending' ||
              data.receiptEmailStatus === 'failed'
                ? data.receiptEmailStatus
                : undefined,
            receiptEmailFailureReason:
              typeof data.receiptEmailFailureReason === 'string'
                ? data.receiptEmailFailureReason
                : null,
            donorIdentityStatus:
              data.donorIdentityStatus === 'guest_unclaimed' || data.donorIdentityStatus === 'claimed'
                ? data.donorIdentityStatus
                : undefined,
          } as DonationRow;
        });

        mapped.sort((a, b) => {
          const aMs = a.donatedAt?.toDate?.().getTime?.() ?? 0;
          const bMs = b.donatedAt?.toDate?.().getTime?.() ?? 0;
          return bMs - aMs;
        });

        setDonations(mapped);
        setLoading(false);
      }, () => {
        setDonations([]);
        setLoading(false);
      })
    );

    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [userId]);

  const recurringGiftRows = useMemo(() => {
    const seenSubscriptionIds = new Set<string>();
    const fromDonations = donationsWithEffectiveFrequency
      .filter((donation) => donation.donationFrequency === 'monthly')
      .flatMap((donation) => {
        const subscriptionId = donation.stripeSubscriptionId?.trim();
        if (subscriptionId) {
          if (seenSubscriptionIds.has(subscriptionId)) {
            return [];
          }
          seenSubscriptionIds.add(subscriptionId);
          return [
            {
              id: subscriptionId,
              label: subscriptionId,
              status: donation.status === 'paid' ? 'active' : donation.status,
              nextBilling: null,
              source: 'donation' as const,
            },
          ];
        }

        return [
          {
            id: donation.id,
            label: `Monthly gift · ${formatMoney(donation.amount, donation.currency)}/month`,
            status: donation.status === 'paid' ? 'active' : donation.status,
            nextBilling: null,
            source: 'donation' as const,
          },
        ];
      });

    if (fromDonations.length > 0) {
      return fromDonations;
    }

    if (donorRecord && donorRecord.monthlyDonationCount > 0) {
      return [
        {
          id: 'donor-rollup-monthly',
          label: `Monthly gift · ${formatMoney(donorRecord.lastDonationAmount, donorRecord.currency)}/month`,
          status: 'active',
          nextBilling: null,
          source: 'donation' as const,
        },
      ];
    }

    return [];
  }, [donationsWithEffectiveFrequency, donorRecord]);

  const donorSummary = useMemo(() => {
    if (!donorRecord) return null;
    return {
      totalDonated: formatMoney(donorRecord.totalDonated, donorRecord.currency),
      monthlyCount: donorRecord.monthlyDonationCount,
      oneTimeCount: donorRecord.oneTimeDonationCount,
    };
  }, [donorRecord]);

  useEffect(() => {
    pushAnalyticsEvent({
      event: 'donor_dashboard_first_visit',
      page: 'dashboard_billing',
    });
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 space-y-8">
      <section className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Giving & receipts</h1>
            <p className="text-sm text-gray-600 mt-1">
              Track your GSGO giving history and monthly support in one place.
            </p>
          </div>
          <Link
            to="/donate"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Heart size={15} />
            Make a donation
          </Link>
        </div>
        {warriorNumber != null && (donorRecord?.monthlyDonationCount ?? 0) > 0 ? (
          <div className="mt-4">
            <MonthlyWarriorTeaser warriorNumber={warriorNumber} />
          </div>
        ) : null}
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Giving snapshot</h2>
        {!donorSummary ? (
          <p className="text-sm text-gray-600">
            We will show your giving totals as soon as your donation records are available.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            <article className="rounded-xl border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Lifetime giving</p>
              <p className="mt-2 text-xl font-semibold text-gray-900">{donorSummary.totalDonated}</p>
            </article>
            <article className="rounded-xl border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Monthly gifts</p>
              <p className="mt-2 text-xl font-semibold text-gray-900">{donorSummary.monthlyCount}</p>
            </article>
            <article className="rounded-xl border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">One-time gifts</p>
              <p className="mt-2 text-xl font-semibold text-gray-900">{donorSummary.oneTimeCount}</p>
            </article>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Donation History</h2>
        {enrichingDonationFrequencies && donations.length > 0 ? (
          <p className="text-sm text-gray-600 mb-3">Refreshing gift details from Stripe…</p>
        ) : null}
        {loading ? (
          <p className="text-sm text-gray-600">Loading your giving history...</p>
        ) : donations.length === 0 ? (
          <p className="text-sm text-gray-600">No donations yet.</p>
        ) : (
          <div className="space-y-3">
            {donationsWithEffectiveFrequency.map((donation, index) => (
              <div key={donation.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Gift #{donationsWithEffectiveFrequency.length - index}</p>
                    <p className="text-xs text-gray-600">
                      {donation.status} · {donation.donationFrequency === 'monthly' ? 'Monthly' : 'One-time'} ·
                      Donated: {formatDate(donation.donatedAt)}
                    </p>
                    <p className="text-xs text-gray-600">
                      Receipt:{' '}
                      {donation.receiptEmailSentAt
                        ? `Sent ${formatDate(donation.receiptEmailSentAt)}`
                        : donation.receiptEmailStatus === 'failed'
                          ? 'Delivery issue, our team will retry'
                          : 'Pending'}
                    </p>
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatMoney(donation.amount, donation.currency)}
                    {donation.donationFrequency === 'monthly' ? '/mo' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recurring gifts</h2>
        {recurringGiftRows.length === 0 ? (
          <p className="text-sm text-gray-600">No recurring monthly gifts yet.</p>
        ) : (
          <div className="space-y-3">
            {recurringGiftRows.map((gift) => (
              <div key={gift.id} className="rounded-xl border border-gray-200 p-4">
                <p className="text-sm font-semibold text-gray-900">{gift.label}</p>
                <p className="text-xs text-gray-600">
                  Status: {gift.status}
                  {gift.nextBilling ? ` · Next billing: ${formatDate(gift.nextBilling)}` : null}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
